import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Select, Card, Space, Typography, Tag, Spin, Empty, DatePicker,
  Table, Button, Tooltip, Drawer, Descriptions, Badge, Tabs
} from 'antd';
import {
  ExperimentOutlined,
  FilterOutlined,
  ReloadOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
// import { skillInsightApi } from '../../services/api';
// 注：后端接口尚未就绪，当前页面走本地 mock 数据。
// 接口完成后，取消上面 import 注释并将 USE_MOCK 设为 false 即可。
const USE_MOCK = true;

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 评测等级定义
const EVAL_LEVELS = [
  { value: 'L0', label: 'L0 静态质量', color: '#8c8c8c' },
  { value: 'L1', label: 'L1 基础验证', color: '#4F8EF7' },
  { value: 'L2', label: 'L2 功能鲁棒', color: '#FA8C16' },
  { value: 'L3', label: 'L3 安全效能', color: '#52C41A' },
];

const LEVEL_COLOR_MAP = {
  L0: '#8c8c8c',
  L1: '#4F8EF7',
  L2: '#FA8C16',
  L3: '#52C41A',
};

// Skill 类型分类
const SKILL_CATEGORIES = [
  { value: 'query', label: '查询检索类' },
  { value: 'review', label: '审查审核类' },
  { value: 'generation', label: '生成创作类' },
  { value: 'recommendation', label: '推荐匹配类' },
  { value: 'knowledge_qa', label: '知识问答类' },
  { value: 'other', label: '其他类' },
];

// 图表颜色池
const LINE_COLORS = [
  '#4F8EF7', '#FF6B6B', '#52C41A', '#FAAD14', '#722ED1',
  '#13C2C2', '#EB2F96', '#FA8C16', '#A0D911', '#1890FF',
];

function SkillInsight() {
  // 筛选状态
  const [domains, setDomains] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState(['L0', 'L1', 'L2', 'L3']);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  // 数据状态
  const [loading, setLoading] = useState(false);
  const [rawRecords, setRawRecords] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [evalSetData, setEvalSetData] = useState([]);
  const [evalSetLoading, setEvalSetLoading] = useState(false);
  // 趋势图随机种子，刷新按钮可改变它从而重新生成趋势抖动
  const [trendSeed, setTrendSeed] = useState(1);

  // 初始化加载筛选项 + 数据
  useEffect(() => {
    loadFilterOptions();
    loadData();
  }, []);

  const loadFilterOptions = async () => {
    // 直接加载本地 mock 选项，不发起请求
    setDomains([
      { value: 'legal', label: '法务域' },
      { value: 'procurement', label: '采购域' },
      { value: 'esg', label: 'ESG域' },
      { value: 'finance', label: '财务域' },
    ]);
    setSkills([
      { value: 'contract-review', label: '合同条款审查', category: 'review', domain: 'legal' },
      { value: 'patent-search', label: '专利信息检索', category: 'query', domain: 'legal' },
      { value: 'supplier-match', label: '供应商推荐匹配', category: 'recommendation', domain: 'procurement' },
      { value: 'bid-doc-gen', label: '招标文档生成', category: 'generation', domain: 'procurement' },
      { value: 'compliance-qa', label: '合规知识问答', category: 'knowledge_qa', domain: 'legal' },
      { value: 'risk-audit', label: '供应商风险稽核', category: 'review', domain: 'procurement' },
      { value: 'esg-report-gen', label: 'ESG报告生成', category: 'generation', domain: 'esg' },
      { value: 'invoice-query', label: '发票信息查询', category: 'query', domain: 'finance' },
    ]);
  };

  const loadData = async () => {
    setLoading(true);
    // 直接走本地 mock，不发起请求
    setTimeout(() => {
      setRawRecords(generateMockTableData());
      setTrendSeed(s => s + 1);
      setLoading(false);
    }, 200);
  };

  // 查看评测详情
  const handleViewDetail = async (record) => {
    setCurrentRecord(record);
    setDrawerVisible(true);
    setEvalSetLoading(true);
    // 直接走本地 mock
    setTimeout(() => {
      setEvalSetData(generateMockEvalSetData(record));
      setEvalSetLoading(false);
    }, 200);
  };

  // 根据领域筛选 Skill 列表
  const filteredSkills = useMemo(() => {
    if (selectedDomains.length === 0) return skills;
    return skills.filter(s => selectedDomains.includes(s.domain));
  }, [skills, selectedDomains]);

  // 根据所有筛选条件实时过滤表格数据
  const filteredTableData = useMemo(() => {
    return rawRecords.filter(r => {
      if (selectedDomains.length && !selectedDomains.includes(r.domainCode)) return false;
      if (selectedCategories.length && !selectedCategories.includes(r.category)) return false;
      if (selectedSkills.length && !selectedSkills.includes(r.skillCode)) return false;
      if (selectedLevels.length && !selectedLevels.includes(r.level)) return false;
      return true;
    });
  }, [rawRecords, selectedDomains, selectedCategories, selectedSkills, selectedLevels]);

  // 基于过滤后的表格数据，按 Skill-等级 组合动态生成趋势数据
  const trendData = useMemo(() => {
    if (!filteredTableData.length) return [];
    const combos = [];
    const seen = new Set();
    for (const r of filteredTableData) {
      const key = `${r.skillName}-${r.level}`;
      if (!seen.has(key)) {
        seen.add(key);
        combos.push({ key, baseRate: r.passRate });
      }
      if (combos.length >= 8) break;
    }
    const data = [];
    for (let i = 29; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day').format('MM-DD');
      const point = { date };
      combos.forEach((c, idx) => {
        const noise = (seededRandom(i * 131 + idx * 17 + trendSeed * 7) - 0.5) * 14;
        point[c.key] = Math.min(100, Math.max(40, Math.round(c.baseRate + noise)));
      });
      data.push(point);
    }
    return data;
  }, [filteredTableData, trendSeed]);

  // 构建趋势图的线条（按等级配色，避免颜色乱）
  const trendLines = useMemo(() => {
    if (!trendData.length) return [];
    const keys = Object.keys(trendData[0] || {}).filter(k => k !== 'date');
    return keys.map((key, idx) => {
      const lv = key.match(/-(L[0-3])$/)?.[1];
      return {
        dataKey: key,
        color: (lv && LEVEL_COLOR_MAP[lv]) || LINE_COLORS[idx % LINE_COLORS.length],
        name: key,
      };
    });
  }, [trendData]);

  // 表格列
  const columns = [
    {
      title: 'Skill名称',
      dataIndex: 'skillName',
      key: 'skillName',
      width: 180,
      fixed: 'left',
    },
    {
      title: '所属领域',
      dataIndex: 'domain',
      key: 'domain',
      width: 100,
      render: (val) => <Tag>{val}</Tag>,
    },
    {
      title: '类型',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (val) => {
        const cat = SKILL_CATEGORIES.find(c => c.value === val);
        return <Tag color="blue">{cat?.label || val}</Tag>;
      },
    },
    {
      title: '评测等级',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (val) => (
        <Tag color={LEVEL_COLOR_MAP[val] || '#666'}>{val}</Tag>
      ),
    },
    {
      title: '符合率',
      dataIndex: 'passRate',
      key: 'passRate',
      width: 100,
      sorter: (a, b) => a.passRate - b.passRate,
      render: (val) => {
        const color = val >= 90 ? '#52C41A' : val >= 70 ? '#FA8C16' : '#FF4D4F';
        return <Text style={{ color, fontWeight: 600 }}>{val}%</Text>;
      },
    },
    {
      title: 'Pass@3',
      dataIndex: 'passAt3',
      key: 'passAt3',
      width: 100,
      render: (val) => val != null ? `${val}%` : '-',
    },
    {
      title: '用例数',
      dataIndex: 'caseCount',
      key: 'caseCount',
      width: 80,
    },
    {
      title: '评测时间',
      dataIndex: 'evalTime',
      key: 'evalTime',
      width: 170,
      sorter: (a, b) => dayjs(a.evalTime).unix() - dayjs(b.evalTime).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: '评测来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (val) => <Tag color="geekblue">{val || '外部平台'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
          查看详情
        </Button>
      ),
    },
  ];

  // 评测集详情表列
  const evalSetColumns = [
    { title: '用例ID', dataIndex: 'caseId', key: 'caseId', width: 100 },
    { title: '用例名称', dataIndex: 'caseName', key: 'caseName', width: 200 },
    {
      title: '结果',
      dataIndex: 'result',
      key: 'result',
      width: 80,
      render: (val) => (
        <Badge status={val === 'pass' ? 'success' : 'error'} text={val === 'pass' ? '通过' : '未通过'} />
      ),
    },
    { title: '输入摘要', dataIndex: 'inputSummary', key: 'inputSummary', ellipsis: true },
    { title: '预期输出', dataIndex: 'expectedOutput', key: 'expectedOutput', ellipsis: true },
    { title: '实际输出', dataIndex: 'actualOutput', key: 'actualOutput', ellipsis: true },
    { title: '耗时(ms)', dataIndex: 'latency', key: 'latency', width: 90 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          Skill 评测洞察
        </Title>
      </div>

      {/* 统一筛选区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Space>
            <Text strong>领域：</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="全部领域"
              style={{ minWidth: 220 }}
              value={selectedDomains}
              onChange={setSelectedDomains}
              options={domains}
              maxTagCount={2}
            />
          </Space>
          <Space>
            <Text strong>Skill类型：</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="全部类型"
              style={{ minWidth: 220 }}
              value={selectedCategories}
              onChange={setSelectedCategories}
              options={SKILL_CATEGORIES}
              maxTagCount={2}
            />
          </Space>
          <Space>
            <Text strong>Skill：</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="全部Skill"
              style={{ minWidth: 240 }}
              value={selectedSkills}
              onChange={setSelectedSkills}
              options={filteredSkills.map(s => ({ value: s.value, label: s.label }))}
              maxTagCount={2}
            />
          </Space>
          <Space>
            <Text strong>评测等级：</Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="全部等级"
              style={{ minWidth: 220 }}
              value={selectedLevels}
              onChange={setSelectedLevels}
              options={EVAL_LEVELS.map(l => ({ value: l.value, label: l.label }))}
              maxTagCount={2}
            />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        </Space>
      </Card>

      {/* 趋势图 */}
      <Card
        title="评测趋势"
        size="small"
        style={{ marginBottom: 16 }}
        extra={<Text type="secondary">横轴：时间 | 纵轴：符合率(%)</Text>}
      >
        <Spin spinning={loading}>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis domain={[0, 100]} unit="%" fontSize={12} />
                <RechartsTooltip />
                <Legend />
                {trendLines.map((line) => (
                  <Line
                    key={line.dataKey}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={line.name}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="暂无趋势数据" style={{ padding: '40px 0' }} />
          )}
        </Spin>
      </Card>

      {/* 评测记录表 */}
      <Card title={`评测记录（${filteredTableData.length} 条）`} size="small">
        <Table
          columns={columns}
          dataSource={filteredTableData}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
        />
      </Card>

      {/* 评测集详情 Drawer */}
      <Drawer
        title={currentRecord ? `${currentRecord.skillName} - ${currentRecord.level} 评测详情` : '评测详情'}
        placement="right"
        width={900}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {currentRecord && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Skill名称">{currentRecord.skillName}</Descriptions.Item>
              <Descriptions.Item label="所属领域">{currentRecord.domain}</Descriptions.Item>
              <Descriptions.Item label="评测等级">
                <Tag color={LEVEL_COLOR_MAP[currentRecord.level]}>{currentRecord.level}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="符合率">
                <Text strong style={{ color: currentRecord.passRate >= 90 ? '#52C41A' : '#FA8C16' }}>
                  {currentRecord.passRate}%
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="评测时间">{currentRecord.evalTime}</Descriptions.Item>
              <Descriptions.Item label="评测来源">{currentRecord.source || '外部平台'}</Descriptions.Item>
              <Descriptions.Item label="用例总数">{currentRecord.caseCount}</Descriptions.Item>
              <Descriptions.Item label="Pass@3">{currentRecord.passAt3 != null ? `${currentRecord.passAt3}%` : '-'}</Descriptions.Item>
            </Descriptions>

            <Title level={5}>评测集明细</Title>
            <Table
              columns={evalSetColumns}
              dataSource={evalSetData}
              rowKey="caseId"
              loading={evalSetLoading}
              size="small"
              scroll={{ x: 900 }}
              pagination={{ pageSize: 10 }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}

// ---- Mock 数据生成 ----
// 基于种子的伪随机，让趋势图形状稳定，刷新才会变化
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function generateMockTableData() {
  const records = [
    { id: '1', skillName: '合同条款审查', skillCode: 'contract-review', domain: '法务域', domainCode: 'legal', category: 'review', level: 'L1', passRate: 92, passAt3: 99, caseCount: 50, evalTime: '2026-05-17 14:30:00', source: 'SkillsBench' },
    { id: '2', skillName: '合同条款审查', skillCode: 'contract-review', domain: '法务域', domainCode: 'legal', category: 'review', level: 'L2', passRate: 85, passAt3: 96, caseCount: 40, evalTime: '2026-05-17 14:30:00', source: 'SkillsBench' },
    { id: '3', skillName: '专利信息检索', skillCode: 'patent-search', domain: '法务域', domainCode: 'legal', category: 'query', level: 'L1', passRate: 88, passAt3: 97, caseCount: 30, evalTime: '2026-05-16 10:00:00', source: 'SkillsBench' },
    { id: '4', skillName: '专利信息检索', skillCode: 'patent-search', domain: '法务域', domainCode: 'legal', category: 'query', level: 'L2', passRate: 76, passAt3: 91, caseCount: 30, evalTime: '2026-05-16 10:00:00', source: 'SkillsBench' },
    { id: '5', skillName: '供应商推荐匹配', skillCode: 'supplier-match', domain: '采购域', domainCode: 'procurement', category: 'recommendation', level: 'L1', passRate: 94, passAt3: 100, caseCount: 25, evalTime: '2026-05-15 16:20:00', source: 'SkillsBench' },
    { id: '6', skillName: '招标文档生成', skillCode: 'bid-doc-gen', domain: '采购域', domainCode: 'procurement', category: 'generation', level: 'L2', passRate: 72, passAt3: 88, caseCount: 20, evalTime: '2026-05-14 09:00:00', source: 'SkillsBench' },
    { id: '7', skillName: '合规知识问答', skillCode: 'compliance-qa', domain: '法务域', domainCode: 'legal', category: 'knowledge_qa', level: 'L1', passRate: 90, passAt3: 98, caseCount: 45, evalTime: '2026-05-13 11:30:00', source: 'SkillsBench' },
    { id: '8', skillName: '供应商风险稽核', skillCode: 'risk-audit', domain: '采购域', domainCode: 'procurement', category: 'review', level: 'L3', passRate: 81, passAt3: 93, caseCount: 35, evalTime: '2026-05-12 15:00:00', source: 'SkillsBench' },
    { id: '9', skillName: 'ESG报告生成', skillCode: 'esg-report-gen', domain: 'ESG域', domainCode: 'esg', category: 'generation', level: 'L1', passRate: 87, passAt3: 95, caseCount: 15, evalTime: '2026-05-11 08:30:00', source: 'SkillsBench' },
    { id: '10', skillName: '发票信息查询', skillCode: 'invoice-query', domain: '财务域', domainCode: 'finance', category: 'query', level: 'L1', passRate: 96, passAt3: 100, caseCount: 60, evalTime: '2026-05-10 13:00:00', source: 'SkillsBench' },
    { id: '11', skillName: '合同条款审查', skillCode: 'contract-review', domain: '法务域', domainCode: 'legal', category: 'review', level: 'L0', passRate: 98, passAt3: null, caseCount: 80, evalTime: '2026-05-17 14:30:00', source: 'SkillsBench' },
    { id: '12', skillName: '专利信息检索', skillCode: 'patent-search', domain: '法务域', domainCode: 'legal', category: 'query', level: 'L3', passRate: 68, passAt3: 84, caseCount: 25, evalTime: '2026-05-16 10:00:00', source: 'SkillsBench' },
    { id: '13', skillName: 'ESG报告生成', skillCode: 'esg-report-gen', domain: 'ESG域', domainCode: 'esg', category: 'generation', level: 'L2', passRate: 79, passAt3: 90, caseCount: 18, evalTime: '2026-05-11 08:30:00', source: 'SkillsBench' },
    { id: '14', skillName: '供应商推荐匹配', skillCode: 'supplier-match', domain: '采购域', domainCode: 'procurement', category: 'recommendation', level: 'L2', passRate: 89, passAt3: 96, caseCount: 22, evalTime: '2026-05-15 16:20:00', source: 'SkillsBench' },
    { id: '15', skillName: '发票信息查询', skillCode: 'invoice-query', domain: '财务域', domainCode: 'finance', category: 'query', level: 'L2', passRate: 91, passAt3: 98, caseCount: 50, evalTime: '2026-05-10 13:00:00', source: 'SkillsBench' },
  ];
  return records;
}

function generateMockEvalSetData(record) {
  const results = [];
  const count = record?.caseCount || 20;
  for (let i = 1; i <= Math.min(count, 20); i++) {
    results.push({
      caseId: `TC-${String(i).padStart(3, '0')}`,
      caseName: `${record?.skillName || 'Skill'}测试用例${i}`,
      result: Math.random() > 0.15 ? 'pass' : 'fail',
      inputSummary: `测试输入场景${i}的描述内容...`,
      expectedOutput: `期望输出结果${i}`,
      actualOutput: `实际输出结果${i}`,
      latency: Math.round(800 + Math.random() * 3000),
    });
  }
  return results;
}

export default SkillInsight;
